import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { projectsApi, clientsApi, templatesApi } from '../../api/client';
import { Department, ProjectTemplate, Client } from '../../types';
import {
  ChevronLeft, ChevronRight, Check, Plus, X, GripVertical,
  AlertTriangle, Calendar, User, FileText, Layers, Bell,
} from 'lucide-react';

// ─── Design tokens ───────────────────────────────────────────────────────────

const C = {
  bg: '#0a0a0f',
  card: '#12121a',
  border: '#1e1e2e',
  borderHover: '#2e2e42',
  textPrimary: '#e2e1f0',
  textSecondary: '#6b6880',
  textMuted: '#4a4858',
  orange: '#f97316',
  orangeHover: '#ea580c',
  success: '#22c55e',
  error: '#ef4444',
  warning: '#f59e0b',
};

// ─── Departments ─────────────────────────────────────────────────────────────

const ALL_DEPTS: Department[] = ['gestor', 'financiero', 'imprenta', 'dtf', 'almacen', 'embalaje'];

const DEPT_COLORS: Record<Department, string> = {
  gestor: '#3b82f6',
  financiero: '#8b5cf6',
  imprenta: '#ec4899',
  dtf: '#f97316',
  almacen: '#22c55e',
  embalaje: '#14b8a6',
};

const DEPT_LABELS: Record<Department, string> = {
  gestor: 'Gestor',
  financiero: 'Financiero',
  imprenta: 'Imprenta',
  dtf: 'DTF',
  almacen: 'Almacén',
  embalaje: 'Embalaje',
};

// ─── Priority config ──────────────────────────────────────────────────────────

const PRIORITIES = [
  { value: 'low',    label: 'Baja',     color: '#22c55e' },
  { value: 'normal', label: 'Normal',   color: '#3b82f6' },
  { value: 'high',   label: 'Alta',     color: '#f59e0b' },
  { value: 'urgent', label: 'Urgente',  color: '#ef4444' },
];

// ─── Shared input styles ──────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: '#0a0a0f',
  border: '1px solid #1e1e2e',
  borderRadius: 8,
  color: C.textPrimary,
  fontSize: 13,
  padding: '10px 12px',
  fontFamily: 'DM Sans, sans-serif',
  outline: 'none',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: C.textMuted,
  letterSpacing: '0.06em',
  fontFamily: 'JetBrains Mono, monospace',
  display: 'block',
  marginBottom: 6,
};

// ─── Step indicator ───────────────────────────────────────────────────────────

function StepIndicator({ step, current, isMobile }: { step: number; current: number; isMobile: boolean }) {
  const steps = ['Info básica', 'Flujo', 'Revisión'];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 36 }}>
      {steps.map((label, i) => {
        const num = i + 1;
        const active = num === current;
        const done = num < current;
        return (
          <React.Fragment key={label}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              <div style={{
                width: isMobile ? 28 : 32, height: isMobile ? 28 : 32, borderRadius: '50%',
                background: done ? C.success : active ? C.orange : '#1e1e2e',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: isMobile ? 11 : 13, fontWeight: 700,
                color: (done || active) ? '#fff' : C.textMuted,
                transition: 'all 0.3s',
                boxShadow: active ? `0 0 16px rgba(249,115,22,0.4)` : 'none',
              }}>
                {done ? <Check size={isMobile ? 12 : 14} /> : num}
              </div>
              {!isMobile && (
                <span style={{ fontSize: 11, color: active ? C.orange : done ? C.success : C.textMuted, whiteSpace: 'nowrap', fontFamily: 'DM Sans, sans-serif', fontWeight: active ? 600 : 400 }}>
                  {label}
                </span>
              )}
            </div>
            {i < steps.length - 1 && (
              <div style={{ flex: 1, height: 1, background: done ? C.success : '#1e1e2e', margin: isMobile ? '0 8px' : '0 12px', marginBottom: isMobile ? 0 : 22, transition: 'background 0.3s' }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Dept chip for flow builder ───────────────────────────────────────────────

function DeptChip({ dept, onRemove, dragging }: { dept: Department; onRemove?: () => void; dragging?: boolean }) {
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '6px 10px', borderRadius: 8,
      background: `${DEPT_COLORS[dept]}18`,
      border: `1px solid ${DEPT_COLORS[dept]}40`,
      color: DEPT_COLORS[dept],
      fontSize: 12, fontWeight: 600, fontFamily: 'DM Sans, sans-serif',
      cursor: dragging ? 'grabbing' : 'grab',
      userSelect: 'none',
      opacity: dragging ? 0.5 : 1,
    }}>
      <GripVertical size={12} style={{ color: DEPT_COLORS[dept] }} />
      {DEPT_LABELS[dept]}
      {onRemove && (
        <button onClick={onRemove} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', color: DEPT_COLORS[dept] }}>
          <X size={12} />
        </button>
      )}
    </div>
  );
}

// ─── Mini client form ─────────────────────────────────────────────────────────

function InlineClientForm({ onCreated, onCancel }: { onCreated: (c: Client) => void; onCancel: () => void }) {
  const [form, setForm] = useState({ name: '', company: '', email: '', phone: '' });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const result = await clientsApi.create(form);
      onCreated(result);
    } catch {
      // Fallback: create mock client
      const mock: Client = { id: Date.now(), name: form.name, company: form.company || undefined, email: form.email || undefined, phone: form.phone || undefined, createdAt: new Date().toISOString() };
      onCreated(mock);
    } finally {
      setSaving(false);
    }
  };

  const field = (key: keyof typeof form, label: string, placeholder: string, required?: boolean) => (
    <div>
      <label style={labelStyle}>{label}{required && ' *'}</label>
      <input
        style={inputStyle}
        placeholder={placeholder}
        value={form[key]}
        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
      />
    </div>
  );

  return (
    <div style={{ padding: 16, background: '#0e0e16', border: `1px solid ${C.border}`, borderRadius: 10, marginTop: 8 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: C.textSecondary, marginBottom: 12 }}>Nuevo cliente</div>
      <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth < 768 ? '1fr' : '1fr 1fr', gap: 10 }}>
        {field('name', 'Nombre', 'Carlos García', true)}
        {field('company', 'Empresa', 'Moda Express SL')}
        {field('email', 'Email', 'carlos@empresa.es')}
        {field('phone', 'Teléfono', '612345678')}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button onClick={handleSave} disabled={saving || !form.name.trim()} style={{
          padding: '7px 14px', borderRadius: 7, border: 'none', cursor: 'pointer',
          background: C.orange, color: '#fff', fontSize: 12, fontFamily: 'DM Sans, sans-serif', fontWeight: 600,
          opacity: saving || !form.name.trim() ? 0.6 : 1,
        }}>
          {saving ? 'Guardando...' : 'Crear'}
        </button>
        <button onClick={onCancel} style={{ padding: '7px 14px', borderRadius: 7, border: `1px solid ${C.border}`, cursor: 'pointer', background: 'transparent', color: C.textSecondary, fontSize: 12, fontFamily: 'DM Sans, sans-serif' }}>
          Cancelar
        </button>
      </div>
    </div>
  );
}

// ─── Step 1: Basic info ───────────────────────────────────────────────────────

function Step1({ form, setForm, clients, templates, clientSearch, setClientSearch, showClientForm, setShowClientForm, onClientCreated, isMobile }: any) {
  const filteredClients = useMemo(() => {
    if (!clientSearch) return clients;
    const q = clientSearch.toLowerCase();
    return clients.filter((c: Client) =>
      c.name.toLowerCase().includes(q) || (c.company || '').toLowerCase().includes(q)
    );
  }, [clients, clientSearch]);

  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const selectedClient = clients.find((c: Client) => c.id === form.clientId);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Title */}
      <div>
        <label style={labelStyle}>TÍTULO DEL PROYECTO *</label>
        <input
          style={inputStyle}
          placeholder="Ej: Camisetas DTF Temporada Verano"
          value={form.title}
          onChange={e => setForm((f: any) => ({ ...f, title: e.target.value }))}
        />
      </div>

      {/* Description */}
      <div>
        <label style={labelStyle}>DESCRIPCIÓN</label>
        <textarea
          style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }}
          placeholder="Descripción del proyecto..."
          value={form.description}
          onChange={e => setForm((f: any) => ({ ...f, description: e.target.value }))}
        />
      </div>

      {/* Priority + Due date */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
        <div>
          <label style={labelStyle}>PRIORIDAD</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {PRIORITIES.map(p => (
              <button
                key={p.value}
                onClick={() => setForm((f: any) => ({ ...f, priority: p.value }))}
                style={{
                  flex: 1, padding: '8px 4px', borderRadius: 8, cursor: 'pointer',
                  background: form.priority === p.value ? `${p.color}22` : '#0a0a0f',
                  border: form.priority === p.value ? `1px solid ${p.color}80` : `1px solid ${C.border}`,
                  color: form.priority === p.value ? p.color : C.textMuted,
                  fontSize: 11, fontWeight: 600, fontFamily: 'DM Sans, sans-serif',
                  transition: 'all 0.15s',
                }}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label style={labelStyle}>FECHA LÍMITE</label>
          <input
            type="date"
            style={{ ...inputStyle, colorScheme: 'dark' }}
            value={form.dueDate}
            onChange={e => setForm((f: any) => ({ ...f, dueDate: e.target.value }))}
          />
        </div>
      </div>

      {/* Client selector */}
      <div style={{ position: 'relative' }}>
        <label style={labelStyle}>CLIENTE</label>
        <div style={{ position: 'relative' }}>
          <input
            style={inputStyle}
            placeholder="Buscar cliente..."
            value={selectedClient ? (selectedClient.company ? `${selectedClient.name} — ${selectedClient.company}` : selectedClient.name) : clientSearch}
            onChange={e => {
              setClientSearch(e.target.value);
              setForm((f: any) => ({ ...f, clientId: undefined }));
              setShowClientDropdown(true);
            }}
            onFocus={() => setShowClientDropdown(true)}
          />
          {form.clientId && (
            <button onClick={() => { setForm((f: any) => ({ ...f, clientId: undefined })); setClientSearch(''); }} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted }}>
              <X size={14} />
            </button>
          )}
        </div>
        {showClientDropdown && !form.clientId && (
          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden', boxShadow: '0 8px 24px rgba(0,0,0,0.4)', marginTop: 4, maxHeight: 200, overflowY: 'auto' }}>
            {filteredClients.map((c: Client) => (
              <button key={c.id} onClick={() => { setForm((f: any) => ({ ...f, clientId: c.id })); setShowClientDropdown(false); setClientSearch(''); }} style={{ display: 'block', width: '100%', padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', borderBottom: `1px solid ${C.border}` }} onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.03)')} onMouseLeave={e => (e.currentTarget.style.background = 'none')}>
                <div style={{ fontSize: 13, color: C.textPrimary, fontFamily: 'DM Sans, sans-serif' }}>{c.name}</div>
                {c.company && <div style={{ fontSize: 11, color: C.textSecondary }}>{c.company}</div>}
              </button>
            ))}
            <button onClick={() => { setShowClientDropdown(false); setShowClientForm(true); }} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 14px', background: 'rgba(249,115,22,0.05)', border: 'none', cursor: 'pointer', color: C.orange, fontSize: 12, fontFamily: 'DM Sans, sans-serif', fontWeight: 600 }}>
              <Plus size={14} /> Crear nuevo cliente
            </button>
          </div>
        )}
        {showClientDropdown && <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setShowClientDropdown(false)} />}
      </div>

      {showClientForm && (
        <InlineClientForm
          onCreated={c => { onClientCreated(c); setForm((f: any) => ({ ...f, clientId: c.id })); setShowClientForm(false); }}
          onCancel={() => setShowClientForm(false)}
        />
      )}

      {/* Template selector */}
      <div>
        <label style={labelStyle}>PLANTILLA (OPCIONAL)</label>
        <select
          style={{ ...inputStyle, cursor: 'pointer' }}
          value={form.templateId || ''}
          onChange={e => setForm((f: any) => ({ ...f, templateId: e.target.value ? Number(e.target.value) : undefined }))}
        >
          <option value="">Sin plantilla</option>
          {templates.map((t: ProjectTemplate) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
      </div>

      {/* Notifications */}
      <div>
        <label style={labelStyle}>NOTIFICACIONES</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[
            { key: 'notifyClient', label: 'Notificar al cliente', desc: 'El cliente recibe emails sobre el estado del proyecto' },
            { key: 'notifyOnChange', label: 'Notificar en cambios', desc: 'Avisar internamente cuando cambia el estado' },
          ].map(({ key, label, desc }) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', background: '#0a0a0f', border: `1px solid ${C.border}`, borderRadius: 8 }}>
              <div>
                <div style={{ fontSize: 13, color: C.textPrimary, fontFamily: 'DM Sans, sans-serif', fontWeight: 500 }}>{label}</div>
                <div style={{ fontSize: 11, color: C.textMuted }}>{desc}</div>
              </div>
              <button
                onClick={() => setForm((f: any) => ({ ...f, [key]: !f[key] }))}
                style={{
                  width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
                  background: form[key] ? C.orange : '#2a2a3a',
                  position: 'relative', transition: 'background 0.2s', flexShrink: 0,
                }}
              >
                <div style={{
                  position: 'absolute', top: 3, left: form[key] ? 22 : 3,
                  width: 18, height: 18, borderRadius: '50%', background: '#fff',
                  transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                }} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Step 2: Flow builder ─────────────────────────────────────────────────────

function Step2({ flow, setFlow, isMobile }: { flow: Department[]; setFlow: (f: Department[]) => void; isMobile: boolean }) {
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [overIdx, setOverIdx] = useState<number | null>(null);

  const available = ALL_DEPTS.filter(d => !flow.includes(d));

  const handleDragStart = (e: React.DragEvent, idx: number) => {
    e.dataTransfer.effectAllowed = 'move';
    setDragIdx(idx);
  };

  const handleDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setOverIdx(idx);
  };

  const handleDrop = (e: React.DragEvent, targetIdx: number) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === targetIdx) { setDragIdx(null); setOverIdx(null); return; }
    const next = [...flow];
    const [item] = next.splice(dragIdx, 1);
    next.splice(targetIdx, 0, item);
    setFlow(next);
    setDragIdx(null);
    setOverIdx(null);
  };

  const handleAddDept = (dept: Department) => setFlow([...flow, dept]);
  const handleRemove = (idx: number) => setFlow(flow.filter((_, i) => i !== idx));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Available departments */}
      <div>
        <label style={labelStyle}>DEPARTAMENTOS DISPONIBLES</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: 16, background: '#0a0a0f', border: `1px solid ${C.border}`, borderRadius: 10, minHeight: 56 }}>
          {available.length === 0 ? (
            <span style={{ fontSize: 12, color: C.textMuted, fontStyle: 'italic' }}>Todos los departamentos están en el flujo</span>
          ) : available.map(d => (
            <button key={d} onClick={() => handleAddDept(d)} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '6px 12px', borderRadius: 8, border: `1px solid ${DEPT_COLORS[d]}40`,
              background: `${DEPT_COLORS[d]}12`, color: DEPT_COLORS[d],
              fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
              transition: 'all 0.15s',
            }} onMouseEnter={e => (e.currentTarget.style.background = `${DEPT_COLORS[d]}22`)} onMouseLeave={e => (e.currentTarget.style.background = `${DEPT_COLORS[d]}12`)}>
              <Plus size={12} /> {DEPT_LABELS[d]}
            </button>
          ))}
        </div>
      </div>

      {/* Flow */}
      <div>
        <label style={labelStyle}>FLUJO DEL PROYECTO (arrastra para reordenar)</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {flow.length === 0 ? (
            <div style={{ padding: 24, background: '#0a0a0f', border: `2px dashed ${C.border}`, borderRadius: 10, textAlign: 'center', color: C.textMuted, fontSize: 13 }}>
              Añade departamentos para construir el flujo
            </div>
          ) : flow.map((dept, idx) => (
            <div
              key={`${dept}-${idx}`}
              draggable
              onDragStart={e => handleDragStart(e, idx)}
              onDragOver={e => handleDragOver(e, idx)}
              onDrop={e => handleDrop(e, idx)}
              onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '12px 14px',
                background: overIdx === idx ? `${DEPT_COLORS[dept]}10` : C.card,
                border: overIdx === idx ? `1px solid ${DEPT_COLORS[dept]}60` : `1px solid ${C.border}`,
                borderRadius: 10, cursor: 'grab',
                transition: 'all 0.15s',
                opacity: dragIdx === idx ? 0.4 : 1,
              }}
            >
              <div style={{ width: 24, height: 24, borderRadius: 6, background: `${DEPT_COLORS[dept]}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: DEPT_COLORS[dept], fontFamily: 'JetBrains Mono, monospace', flexShrink: 0 }}>
                {idx + 1}
              </div>
              <GripVertical size={14} color={C.textMuted} style={{ flexShrink: 0 }} />
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: DEPT_COLORS[dept], display: 'inline-block', flexShrink: 0 }} />
                <span style={{ fontSize: 13, fontWeight: 600, color: C.textPrimary, fontFamily: 'DM Sans, sans-serif' }}>{DEPT_LABELS[dept]}</span>
              </div>
              <button onClick={() => handleRemove(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, display: 'flex', padding: 4 }}>
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Preview timeline */}
      {flow.length > 0 && (
        <div>
          <label style={labelStyle}>VISTA PREVIA DEL FLUJO</label>
          <div style={{ display: isMobile ? 'flex' : 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? 8 : 0, padding: '16px 20px', background: '#0a0a0f', border: `1px solid ${C.border}`, borderRadius: 10, overflowX: isMobile ? 'hidden' : 'auto' }}>
            {flow.map((dept, idx) => (
              <React.Fragment key={`prev-${dept}-${idx}`}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                  <div style={{ width: 36, height: 36, borderRadius: '50%', background: `${DEPT_COLORS[dept]}22`, border: `2px solid ${DEPT_COLORS[dept]}60`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: DEPT_COLORS[dept], fontFamily: 'JetBrains Mono, monospace' }}>
                    {idx + 1}
                  </div>
                  <span style={{ fontSize: 10, color: DEPT_COLORS[dept], fontWeight: 600, whiteSpace: 'nowrap' }}>{DEPT_LABELS[dept]}</span>
                </div>
                {idx < flow.length - 1 && (
                  <div style={{ flex: '0 0 32px', height: 2, background: `linear-gradient(90deg, ${DEPT_COLORS[dept]}60, ${DEPT_COLORS[flow[idx + 1]]}60)`, margin: '0 4px', marginBottom: 18 }} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Step 3: Review ───────────────────────────────────────────────────────────

function Step3({ form, flow, clients, templates }: any) {
  const client = clients.find((c: Client) => c.id === form.clientId);
  const template = templates.find((t: ProjectTemplate) => t.id === form.templateId);
  const priority = PRIORITIES.find(p => p.value === form.priority);

  const Section = ({ icon: Icon, title, children }: any) => (
    <div style={{ padding: 16, background: '#0a0a0f', border: `1px solid ${C.border}`, borderRadius: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, paddingBottom: 12, borderBottom: `1px solid ${C.border}` }}>
        <div style={{ width: 28, height: 28, borderRadius: 7, background: 'rgba(249,115,22,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={14} color={C.orange} />
        </div>
        <span style={{ fontSize: 12, fontWeight: 700, color: C.textSecondary, letterSpacing: '0.04em', fontFamily: 'DM Sans, sans-serif' }}>{title}</span>
      </div>
      {children}
    </div>
  );

  const Row = ({ label, value, accent }: any) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
      <span style={{ fontSize: 12, color: C.textMuted }}>{label}</span>
      <span style={{ fontSize: 12, color: accent || C.textPrimary, fontWeight: 500 }}>{value || '—'}</span>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Section icon={FileText} title="Info básica">
        <Row label="Título" value={form.title} />
        <Row label="Descripción" value={form.description} />
        <Row label="Prioridad" value={priority?.label} accent={priority?.color} />
        <Row label="Fecha límite" value={form.dueDate ? new Date(form.dueDate).toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' }) : ''} />
        {template && <Row label="Plantilla" value={template.name} />}
      </Section>

      <Section icon={User} title="Cliente">
        {client ? (
          <>
            <Row label="Nombre" value={client.name} />
            {client.company && <Row label="Empresa" value={client.company} />}
            {client.email && <Row label="Email" value={client.email} />}
            {client.phone && <Row label="Teléfono" value={client.phone} />}
          </>
        ) : (
          <div style={{ fontSize: 12, color: C.textMuted, fontStyle: 'italic' }}>Sin cliente asignado</div>
        )}
      </Section>

      <Section icon={Layers} title="Flujo de departamentos">
        {flow.length === 0 ? (
          <div style={{ fontSize: 12, color: C.textMuted, fontStyle: 'italic' }}>No hay departamentos en el flujo</div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {flow.map((dept: Department, idx: number) => (
              <div key={`rev-${dept}-${idx}`} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {idx > 0 && <span style={{ color: C.textMuted, fontSize: 12 }}>→</span>}
                <span style={{ padding: '4px 10px', borderRadius: 6, background: `${DEPT_COLORS[dept]}18`, border: `1px solid ${DEPT_COLORS[dept]}40`, color: DEPT_COLORS[dept], fontSize: 12, fontWeight: 600 }}>
                  {DEPT_LABELS[dept]}
                </span>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section icon={Bell} title="Notificaciones">
        <Row label="Notificar al cliente" value={form.notifyClient ? 'Sí' : 'No'} accent={form.notifyClient ? C.success : undefined} />
        <Row label="Notificar en cambios" value={form.notifyOnChange ? 'Sí' : 'No'} accent={form.notifyOnChange ? C.success : undefined} />
      </Section>
    </div>
  );
}

// ─── Main CreateProjectPage ───────────────────────────────────────────────────

export default function CreateProjectPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [clientSearch, setClientSearch] = useState('');
  const [showClientForm, setShowClientForm] = useState(false);
  const [extraClients, setExtraClients] = useState<Client[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const [form, setForm] = useState({
    title: '',
    description: '',
    priority: 'normal',
    dueDate: '',
    clientId: undefined as number | undefined,
    templateId: undefined as number | undefined,
    notifyClient: false,
    notifyOnChange: true,
  });

  const [flow, setFlow] = useState<Department[]>(['gestor']);

  const { data: apiClients } = useQuery({ queryKey: ['clients'], queryFn: () => clientsApi.list(), retry: false });
  const { data: apiTemplates } = useQuery({ queryKey: ['templates'], queryFn: () => templatesApi.list(), retry: false });

  const clients: Client[] = [
    ...(Array.isArray(apiClients) ? apiClients : []),
    ...extraClients,
  ];

  const templates: ProjectTemplate[] = apiTemplates ?? [];

  // Pre-select default template when templates load (only if none selected yet)
  useEffect(() => {
    if (!form.templateId && templates.length > 0) {
      const defaultTpl = templates.find(t => t.isDefault && !t.isArchived);
      if (defaultTpl) {
        const sorted = [...defaultTpl.steps].sort((a, b) => a.order - b.order);
        setForm(f => ({ ...f, templateId: defaultTpl.id }));
        setFlow(sorted.map(s => s.department));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates.length]);

  // When template changes, pre-fill flow
  const handleFormChange = (updater: (f: any) => any) => {
    setForm(prev => {
      const next = updater(prev);
      if (next.templateId !== prev.templateId && next.templateId) {
        const tpl = templates.find(t => t.id === next.templateId);
        if (tpl) {
          const sorted = [...tpl.steps].sort((a, b) => a.order - b.order);
          setFlow(sorted.map(s => s.department));
        }
      }
      return next;
    });
  };

  const { mutate: createProject, isPending } = useMutation({
    mutationFn: () => {
      const payload = {
        title: form.title,
        description: form.description,
        priority: form.priority,
        dueDate: form.dueDate || undefined,
        clientId: form.clientId,
        templateId: form.templateId || undefined,
        steps: flow.map((dept, idx) => ({ department: dept, orderIndex: idx })),
        notifications: { email: form.notifyClient, onStateChange: form.notifyOnChange },
      };
      return projectsApi.create(payload);
    },
    onSuccess: () => navigate('/gestor'),
    onError: (e: any) => {
      setSubmitError(e?.response?.data?.message || 'Error al crear el proyecto. Inténtalo de nuevo.');
    },
  });

  const canNext = () => {
    if (step === 1) return form.title.trim().length > 0;
    if (step === 2) return flow.length > 0;
    return true;
  };

  return (
    <div style={{ padding: isMobile ? '20px 16px' : '32px', minHeight: '100vh', background: C.bg, fontFamily: 'DM Sans, sans-serif', maxWidth: 780, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 36 }}>
        <button onClick={() => navigate('/gestor')} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 12px', cursor: 'pointer', color: C.textSecondary, display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontFamily: 'DM Sans, sans-serif', flexShrink: 0 }}>
          <ChevronLeft size={15} /> {!isMobile && 'Volver'}
        </button>
        <div>
          <h1 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 900, fontSize: isMobile ? 20 : 24, color: '#fff', margin: 0, letterSpacing: '-0.03em' }}>Nuevo Proyecto</h1>
          <p style={{ fontSize: 12, color: C.textSecondary, margin: 0 }}>Completa los pasos para crear un proyecto</p>
        </div>
      </div>

      {/* Card */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: isMobile ? '20px 16px' : '32px 36px' }}>
        <StepIndicator step={step} current={step} isMobile={isMobile} />

        {step === 1 && (
          <Step1
            form={form}
            setForm={handleFormChange}
            clients={clients}
            templates={templates}
            clientSearch={clientSearch}
            setClientSearch={setClientSearch}
            showClientForm={showClientForm}
            setShowClientForm={setShowClientForm}
            onClientCreated={(c: Client) => setExtraClients(prev => [...prev, c])}
            isMobile={isMobile}
          />
        )}
        {step === 2 && <Step2 flow={flow} setFlow={setFlow} isMobile={isMobile} />}
        {step === 3 && <Step3 form={form} flow={flow} clients={clients} templates={templates} />}

        {submitError && (
          <div style={{ marginTop: 20, padding: '10px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, fontSize: 12, color: C.error, display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertTriangle size={14} /> {submitError}
          </div>
        )}

        {/* Navigation */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 36, paddingTop: 24, borderTop: `1px solid ${C.border}` }}>
          <button
            onClick={() => setStep(s => s - 1)}
            style={{
              padding: '10px 20px', borderRadius: 10, border: `1px solid ${C.border}`,
              background: 'transparent', color: C.textSecondary, fontSize: 13,
              cursor: step === 1 ? 'default' : 'pointer',
              fontFamily: 'DM Sans, sans-serif',
              opacity: step === 1 ? 0 : 1, pointerEvents: step === 1 ? 'none' : 'auto',
              display: 'flex', alignItems: 'center', gap: 8,
            }}
          >
            <ChevronLeft size={15} /> Anterior
          </button>

          {step < 3 ? (
            <button
              onClick={() => setStep(s => s + 1)}
              disabled={!canNext()}
              style={{
                padding: '10px 24px', borderRadius: 10, border: 'none',
                background: canNext() ? `linear-gradient(135deg, ${C.orange}, ${C.orangeHover})` : '#2a2a3a',
                color: canNext() ? '#fff' : C.textMuted, fontSize: 13,
                cursor: canNext() ? 'pointer' : 'not-allowed',
                fontFamily: 'DM Sans, sans-serif', fontWeight: 600,
                display: 'flex', alignItems: 'center', gap: 8,
                boxShadow: canNext() ? '0 4px 14px rgba(249,115,22,0.35)' : 'none',
                transition: 'all 0.15s',
              }}
            >
              Siguiente <ChevronRight size={15} />
            </button>
          ) : (
            <button
              onClick={() => createProject()}
              disabled={isPending}
              style={{
                padding: '10px 28px', borderRadius: 10, border: 'none',
                background: isPending ? '#2a2a3a' : `linear-gradient(135deg, ${C.orange}, ${C.orangeHover})`,
                color: isPending ? C.textMuted : '#fff', fontSize: 13,
                cursor: isPending ? 'not-allowed' : 'pointer',
                fontFamily: 'DM Sans, sans-serif', fontWeight: 700,
                display: 'flex', alignItems: 'center', gap: 8,
                boxShadow: isPending ? 'none' : '0 4px 14px rgba(249,115,22,0.4)',
                transition: 'all 0.15s',
              }}
            >
              {isPending ? (
                <>
                  <div style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                  Creando...
                </>
              ) : (
                <><Check size={15} /> Crear Proyecto</>
              )}
            </button>
          )}
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
